// Host settings, plus the authenticated support thread shared by all messengers.
import { useEffect, useState } from "react";
import { useLang, LANGS, LANG_ORDER, dirOf } from "../../i18n.jsx";
import { Btn } from "../../admin/ui.jsx";
import { BACK_PRIORITY, useHostBackButton } from "../backbutton.js";
import { haptic } from "../host.js";
import { api } from "../api.js";
import { C } from "../../design-system.js";
import { ChatComposer, ChatThread } from "../../chat.jsx";
import { guaranteeLeft, useGuarantee } from "./Guarantee.jsx";

// A settings row's "opens something" arrow, pointing the way the page reads.
// It renders inside a dir="ltr" isolate on purpose: ‹ and › are Unicode MIRRORED
// characters, so in an RTL run the browser flips them and «‹» shows up as «›».
function RowChevron({ lang }) {
  return <bdi dir="ltr" style={{ color: C.textMid, fontSize: 18 }}>{dirOf(lang) === "rtl" ? "‹" : "›"}</bdi>;
}

export function SupportSheet({ onClose, subscribed }) {
  const { t, lang } = useLang();
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useHostBackButton(onClose, { priority: BACK_PRIORITY.modal });

  useEffect(() => {
    let alive = true;
    const refresh = () => api("/support/conversation")
      .then(data => { if (alive) { setThread(data); setError(""); } })
      .catch(e => { if (alive) setError(e.message || t("خطا در بارگذاری اطلاعات")); });
    refresh();
    const timer = setInterval(refresh, 12000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  async function send() {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await api("/support/messages", { method: "POST", body: JSON.stringify({ body: text }) });
      setText("");
      setThread(await api("/support/conversation"));
    } catch (e) {
      setError(e.message || t("ارسال ناموفق بود. دوباره تلاش کنید."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: `${C.bgDeep}DD`, display: "flex", alignItems: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, width: "100%", maxWidth: 430, margin: "0 auto", height: "min(86dvh, 720px)", boxSizing: "border-box", padding: "14px 14px calc(14px + var(--tg-safe-bottom, 0px))", borderRadius: "20px 20px 0 0", border: `1px solid ${C.borderGold}`, display: "flex", flexDirection: "column" }}>
        <div style={{ width: 40, height: 4, background: C.border, borderRadius: 4, margin: "0 auto 10px", flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, flexShrink: 0 }}>
          <div>
            <h2 style={{ color: C.cream, fontSize: 18, margin: 0 }}>{t(subscribed ? "پشتیبانی ویژه" : "پشتیبانی")}</h2>
            <p style={{ color: C.textMid, fontSize: 12, margin: "2px 0 0", lineHeight: 1.7 }}>{t("پیامتان را بنویسید؛ پاسخ پشتیبانی همین‌جا نمایش داده می‌شود.")}</p>
          </div>
          <button className="press" onClick={onClose} aria-label={t("بستن")}
            style={{ background: "none", border: "none", color: C.textMid, fontSize: 26, lineHeight: 1, cursor: "pointer", padding: 4 }}>×</button>
        </div>
        <ChatThread messages={thread?.messages || []} mine="user" lang={lang}
          emptyText={t("هنوز پیامی ارسال نشده است")} failedText={t("ارسال پاسخ ناموفق بود")} />
        {error && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{error}</div>}
        <ChatComposer value={text} onChange={setText} onSend={send} busy={busy}
          placeholder={t("پیام شما...")} sendLabel={t("ارسال پیام")} />
      </div>
    </div>
  );
}

export function SettingsSheet({ onClose, onTerms, onSupport, onGuarantee, subscribed }) {
  const { t, lang, setLang } = useLang();
  // The money-back guarantee's time left, right on its row (product owner: the
  // parent sees it under «تنظیمات» on every messenger).
  const [g] = useGuarantee();
  const gOpen = (g?.plans || []).filter(p => p.eligible).sort((a, b) => b.remaining_seconds - a.remaining_seconds)[0];
  const gLine = !g ? "" : g.claim && g.claim.status !== "rejected" ? t("درخواست ثبت شده")
    : g.used ? t("استفاده شده")
    : gOpen ? t("{left} باقی مانده", { left: guaranteeLeft(gOpen.remaining_seconds, t) })
    : t("فعال نیست");
  useHostBackButton(onClose, { priority: BACK_PRIORITY.modal });

  const row = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    width: "100%", background: C.bgDeep, border: `1px solid ${C.border}`,
    borderRadius: 14, padding: "14px 16px", cursor: "pointer",
    fontSize: 15, color: C.cream, textAlign: "start",
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: `${C.bgDeep}DD`, zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center", animation: "fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "24px 20px 40px", animation: "slideUp 0.3s ease", border: `1px solid ${C.borderGold}`, borderBottom: "none", boxSizing: "border-box" }}>
        <div style={{ width: 40, height: 4, background: C.border, borderRadius: 4, margin: "0 auto 20px" }} />
        <h2 style={{ color: C.cream, fontSize: 19, fontWeight: 800, margin: "0 0 18px" }}>{t("تنظیمات")}</h2>
        <div style={{ color: C.textMid, fontSize: 13, marginBottom: 9 }}>{t("زبان")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22 }}>
          {LANG_ORDER.map(code => {
            const on = code === lang;
            return (
              <button key={code} className="press" onClick={() => { haptic("selection"); setLang(code); }}
                style={{ ...row, background: on ? `${C.gold}14` : C.bgDeep, border: `1px solid ${on ? C.gold : C.border}`, color: on ? C.goldLight : C.cream, fontWeight: on ? 700 : 400 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}><span style={{ fontSize: 15 }}>🌐</span><span dir={dirOf(code)}>{LANGS[code].label}</span></span>
                <span style={{ color: on ? C.gold : "transparent", fontSize: 15, fontWeight: 800 }}>✓</span>
              </button>
            );
          })}
        </div>
        <div style={{ color: C.textMid, fontSize: 13, marginBottom: 9 }}>{t("ارتباط با ما")}</div>
        <button className="press" onClick={() => { haptic("light"); onSupport(); }} style={{ ...row, marginBottom: 18 }}>
          <span>{t(subscribed ? "پشتیبانی ویژه" : "پشتیبانی")}</span><RowChevron lang={lang} />
        </button>
        {onGuarantee && (
          <>
            <div style={{ color: C.textMid, fontSize: 13, marginBottom: 9 }}>{t("اشتراک")}</div>
            <button className="press" onClick={() => { haptic("light"); onGuarantee(); }} style={{ ...row, marginBottom: 18 }}>
              <span>{t("ضمانت بازگشت وجه")}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: gOpen ? C.gold : C.textMid, fontSize: 13 }}>{gLine}</span>
                <RowChevron lang={lang} />
              </span>
            </button>
          </>
        )}
        <div style={{ color: C.textMid, fontSize: 13, marginBottom: 9 }}>{t("درباره")}</div>
        <button className="press" onClick={() => { haptic("light"); onTerms(); }} style={row}>
          <span>{t("قوانین و مقررات")}</span><RowChevron lang={lang} />
        </button>
        <Btn variant="outline" color={C.textMid} onClick={onClose} style={{ width: "100%", marginTop: 20 }}>{t("بستن")}</Btn>
      </div>
    </div>
  );
}
